import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  ElementRef,
  OnDestroy,
} from '@angular/core';
import {
  CommunicationManagerService,
  EntityService,
  LocalStorageService,
  ModalService,
  Project,
  ProjectManagerService,
  RegistryEntity,
  ProjectStatusGuardService,
  SessionManagerService,
} from '@ops/shared-types';
import { forkJoin, of, Subscription, switchMap, tap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';

@Component({
  selector: 'ops-project-info-modal',
  templateUrl: './project-info-modal.component.html',
  styleUrls: ['./project-info-modal.component.scss'],
})
export class ProjectInfoModalComponent implements OnInit, OnDestroy {
  @Input() id: string;
  set project(projectInfo: Project | undefined) {
    // console.log(projectInfo as Project);
    // console.log(projectInfo._id);
    if (!projectInfo) {
      this.info = {
        _id: '',
        client: { name: '', _id: '' },
        billingCompany: null,
        code: '',
        status: '0',
        name: '',
      };
      return;
    }
    this.originInfo = { ...projectInfo };
    this.info = {
      _id: projectInfo?._id,
      client: {
        name: projectInfo?.client?.name,
        _id: projectInfo?.client?._id,
      },
      billingCompany: projectInfo.billingCompany,
      code: projectInfo?.code,
      name: projectInfo?.name,
      company: projectInfo?.company,
      versionLink: projectInfo?.versionLink,
      status: projectInfo?.status,
    };
    this.clientName = projectInfo.client.name;
    this.billingCompany = projectInfo.billingCompany?.name || null;
    if (this.billingCompany) {
      this.useDifferentBillingCompany = true;
    }
    this.setClients();
  }

  @Output() projectEmitter: EventEmitter<Project> = new EventEmitter<Project>();

  private element: any;
  info: Project = {
    _id: '',
    client: { name: '', _id: '' },
    billingCompany: null,
    status: '0',
    code: '',
    name: '',
  };
  originInfo: Project;

  clientName: string;
  billingCompany?: string;
  clients: Array<{ name: string; id: string }> = [];

  subClients: Subscription;
  subSave: Subscription;
  subClient: Subscription;
  useDifferentBillingCompany: boolean = false;
  pendingStatusChange: string | null = null; // Track pending status changes
  
  userRoleSub: Subscription;
  userRole: string = 'guest';

  constructor(
    private modalService: ModalService,
    private pm: ProjectManagerService,
    private registryService: EntityService,
    private cm: CommunicationManagerService,
    private el: ElementRef,
    private ls: LocalStorageService,
    private translateService: TranslateService,
    private projectStatusGuard: ProjectStatusGuardService,
    private sessionManager: SessionManagerService,
    private router: Router
  ) {
    this.element = this.el.nativeElement;
    this.element.style.display = 'none';
  }

  get hasDifferentBillingCompany() {
    return this.useDifferentBillingCompany;
  }

  onProjectStatusChange(e) {
    // Store the status change for later save
    this.pendingStatusChange = e.toString();
    this.info.status = e.toString();
    // console.log('Project Info Modal - Status Changed (pending save):', e);
  }

  toggleDifferentBillingCompany() {
    this.useDifferentBillingCompany = !this.useDifferentBillingCompany;

    if (!this.useDifferentBillingCompany) {
      this.billingCompany = null;
    }
  }

  ngOnInit(): void {
    if (!this.id) {
      console.error('modal must have an id');
      return;
    }
    this.element.style.display = 'none';

    if (this.info.company) {
      if (this.subClients) this.subClients.unsubscribe();

      this.subClients = this.registryService
        .getRegitryEntitiesByType(`Client`, this.info.company)
        .subscribe((clients: any) => {
          this.clients = clients.map((c) => {
            return { name: c.name, id: c._id };
          });
          // console.log(this.clients);
        });
    }

    if (!this.originInfo) this.info = { _id: '' };
    else
      this.info = {
        _id: this.originInfo._id,
        client: {
          name: this.originInfo.client.name,
          _id: this.originInfo.client._id,
        },
        billingCompany: this.originInfo.billingCompany,
        code: this.originInfo.code,
        name: this.originInfo.name,
      };

    // console.log('info modal ',this.estimateInfo)

    document.body.appendChild(this.element);

    this.element.addEventListener(
      'click',
      (el: { target: { className: string } }): void => {
        if (el.target.className === 'info-modal') {
          this.close();
        }
      }
    );

    this.userRoleSub = this.sessionManager.getUserRoleObservable().subscribe((role: string) => {
      this.userRole = role;
    });

    this.modalService.add(this);
  }

  setClients() {
    if (this.info.company) {
      if (this.subClients) this.subClients.unsubscribe();

      this.subClients = this.registryService
        .getRegitryEntitiesByType(`Client`, this.info.company)
        .subscribe((clients: any) => {
          this.clients = clients.map((c) => {
            return { name: c.name, id: c._id };
          });
          // console.log(this.clients);
        });
    }
  }

  ngOnDestroy(): void {
    this.modalService.remove(this.id);
    this.element.remove();
  }

  // Smoothly show modal by forcing reflow before applying display:flex
  open(project: Project): void {
    this.element.style.opacity = '0';
    this.element.style.display = 'flex';
    // Force reflow
    void this.element.offsetHeight;
    this.element.style.opacity = '1';
    document.body.classList.add('info-modal-open');
    this.project = project;
  }

  close(reset?: boolean): void {
    if (reset) {
      this.info = { ...this.originInfo };
    }

    this.element.style.display = 'none';
    document.body.classList.remove('info-modal-open');
  }

  

  // Helper function to find a client by name
  findClientByName(name) {
    return this.clients.find(
      (client) => client.name.trim().toLowerCase() === name.trim().toLowerCase()
    );
  }

  // Main function
  handleClients(projectInfo) {
    const clientName = this.clientName.trim();
    const billingCompanyName = this.billingCompany?.trim();

    const findOrCreateClient = (name) => {
      const matchedClient = name ? this.findClientByName(name) : null;
      return matchedClient
        ? of(matchedClient)
        : this.cm
            .postAndGetJson(
              'registryEntity',
              {
                name,
                type: 'Client',
                company: this.info.company,
              },
              [{ headerName: 'companyId', headerValue: this.info.company }]
            )
            .pipe(
              tap((newClient) => {
                // TODO UPDATE LOCAL PROJECT CLIENT ID RELATED TO NEWLY CREATE CLIENT

                this.clients.push({ name: newClient.name, id: newClient._id });
              })
            );
    };

    const client$ = findOrCreateClient(clientName);
    const billingClient$ = this.useDifferentBillingCompany
      ? findOrCreateClient(billingCompanyName)
      : of(null);

    forkJoin({ client: client$, billingCompany: billingClient$ })
      .pipe(
        switchMap(({ client, billingCompany }) => {
          projectInfo.client = client.id;

          this.info.client = client;
          if (billingCompany) {
            projectInfo.billingCompany = billingCompany.id;
            this.info.billingCompany = billingCompany;
          } else {
            projectInfo.billingCompany = null;
            this.info.billingCompany = null;
          }
          return of(projectInfo);
        })
      )
      .subscribe({
        next: (projectInfo) => {
          this.updateProjectInfo(projectInfo);
          this.close();
        },
        error: (error) => {
          window.alert(this.translateService.instant('windowAlert.generic.error', { error: JSON.parse(error.error).message }));
        },
      });
  }

  onSave() {
    if(!window.confirm(this.translateService.instant('windowAlert.projectInfoModal.projcetInfoConfirmUpdate'))) 
      return;
    
    if (this.subSave) this.subSave.unsubscribe();

    // const c = this.clients.find(
    //   (c) => c.name.trim().toLowerCase() == this.clientName.trim().toLowerCase()
    // );

    let newInfo: any = {
      name: this.info.name,
      code: this.info.code,
    };
    this.handleClients(newInfo);
  }

  private updateProjectInfo(info: any) {
    // Prepare the update data including status if there's a pending change
    const updateData: any = {
      name: info.name,
      client: info.client,
      billingCompany: info.billingCompany,
      code: this.info.code,
    };

    // Include status if there's a pending change
    if (this.pendingStatusChange !== null) {
      updateData.status = this.pendingStatusChange;
    }

    this.subSave = this.pm
      .updateProjectinfo(
        this.info.versionLink,
        updateData,
        {
          name: this.info.name,
          client: {
            name: this.info.client.name,
            _id: this.info.client._id || this.info.client['id'],
          },
          billingCompany:
            this.info.billingCompany === null
              ? null
              : {
                  name: this.info.billingCompany.name,
                  _id:
                    this.info.billingCompany._id ||
                    this.info.billingCompany['id'],
                },
          code: this.info.code,
          status: this.info.status, // Include current status in the response data
        },
        this.info._id
      )
      .subscribe({
        next: () => {
          // Clear the pending status change after successful save
          this.pendingStatusChange = null;
          
          // Handle different contexts after successful save
          this.handleContextAfterSave();
          
          window.alert(this.translateService.instant('windowAlert.projectInfoModal.projectInfoUpdated'));
          this.projectEmitter.emit(this.info);
        },
        error: (e) => {
          window.alert(this.translateService.instant('windowAlert.generic.error', { error: JSON.parse(e.error).message }));
        },
      });
  }

  private handleContextAfterSave() {
    // Determine the context based on current route
    const isFromProjectEstimate = this.router.url.includes('project-estimate');
    const isFromProjectInfo = this.router.url.includes('project-info');

    if (isFromProjectEstimate && isFromProjectInfo) {
      // Called from project-estimate/project-info - use the commented code logic
      if (this.info) {
        const updatedProject = { ...this.info };
        // console.log('Project Overview - Updating Guard Service:', { updatedProject });
        
        // Test the service methods directly
        const shouldPrevent = this.projectStatusGuard.shouldApplyPrevention(updatedProject);
        const shouldFullPrevent = this.projectStatusGuard.shouldApplyFullPrevention(updatedProject);
        // console.log('Project Overview - Direct Service Test:', { shouldPrevent, shouldFullPrevent });
        
        this.projectStatusGuard.setCurrentProject(updatedProject);
      } else {
        // console.log('Project Overview - No current project available');
      }
    }
    // For dashboard projects, the projectEmitter.emit(this.info) in updateProjectInfo handles the list update
  }
}
